import { ReactElement, useMemo, useEffect, useState, useRef } from 'react';
import Head from 'next/head';
import { createServerSideHelpers } from '@trpc/react-query/server';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { PublicKey } from "@metaplex-foundation/js"
import { trpc } from 'utils/trpc';
import { toast } from 'react-hot-toast';
import Countdown from "react-countdown";
import styled, { css } from 'styled-components';

import { createContext } from 'server/context';
import superJson from 'utils/superJson';
import { appRouter } from 'server/routers/_app';

import MainLayout from 'components/layout/mainLayout';
import WalletLayout from 'components/layout/walletLayout';
import MaxWidthWrapper from 'components/layout/maxWidthWrapper';
import WalletButton from 'components/common/wallet/walletButton';

import useLayout from 'providers/layout/layoutProvider';
import useIsMounted from 'hooks/useIsMounted';
import theme, { bp, color } from 'settings/theme';

type Props = {};

const CeremonyPage: NextPageExtended<Props> = (props) => {

  const layout = useLayout();
  const wallet = useWallet();
  const { connection } = useConnection();

  const [userSelectedAmount, setUserSelectedAmount] = useState(1); // [0, 23]
  const [selectedRedemptionType, setSelectedRedemptionType] = useState<"Trader" | "Fabric" | "Gladiator">("Trader");
  const [medallionsLockedAmount, setMedallionsLockedAmount] = useState(0);
  const [currentRedemptionsAmount, setCurrentRedemptionsAmount] = useState(0);
  const [currentVaultSize, setCurrentVaultSize] = useState(60.45);
  const ceremonySplit = useMemo(() => {
    return currentVaultSize / (medallionsLockedAmount / 2);
    // return 0.08773584905;
  }, [currentVaultSize, medallionsLockedAmount]);
  const getRedeemTransactionsEndpoint = trpc.web3.getRedeemTokenTransactions.useMutation();
  const getUnstakeTransactionsEndpoint = trpc.web3.getUnstakeTransactions.useMutation();
  const getFinalizeTransactionsEndpoint = trpc.web3.getFinalizeTransactions.useMutation();
  const sendTransactionsEndpoint = trpc.web3.sendTransactions.useMutation();
  const sliderInputRef = useRef<HTMLInputElement>(null);
  // const initiateEndTime = new Date(1676037600000);
  const initiateEndTime = new Date("05 October 2023 24:00:00 GMT")
  // const claimEndTime = new Date(1676124000000);
  // const unstakeEndTime = new Date(1676124000000);
  const unstakeEndTime = new Date("06 October 2023 24:00:00 GMT")
  // const ceremonyEndTime = new Date(1677628800000);
  const ceremonyEndTime = new Date("30 October 2023 24:00:00 GMT")

  const [isRedemptionOpen, setIsRedemptionOpen] = useState(initiateEndTime.getTime() > Date.now());
  const [isUnstakeOpen, setIsUnstakeOpen] = useState(unstakeEndTime.getTime() > Date.now());
  const [status, setStatus] = useState<"idle" | "pending" | "success">("idle");
  // console.log("isRedemptionOpen", isRedemptionOpen);

  const getUserBalancesEndpoint = trpc.web3.getUserBalances.useMutation();
  const [userBalances, setUserBalances] = useState<UserBalances>({} as UserBalances);

  const [displayRedemptions, setDisplayRedemptions] = useState(0);

  const [availableRedemptions, availableUnstakes] = useMemo(() => {
    let availableRedemptions = 0;
    let availableUnstakes = 0;
    if (!wallet.publicKey) {
      return [0, 0];
    }
    if (!userBalances || !userBalances.medallionsHeld || !userBalances.solBalance || !userBalances.gobiBalance || !(userBalances.medallionsHeld > 0 && userBalances.solBalance > 0 && userBalances.gobiBalance > 0)) {

    }
    else {
      availableRedemptions = Math.min(Math.floor(userBalances.medallionsHeld / 2), Math.floor(userBalances.solBalance / 0.003), Math.floor(userBalances.gobiBalance / 200))
    }
    // console.log("userBalances", userBalances);

    if (!userBalances || !(userBalances.traderReceiptBalance > 0 || userBalances.fabricReceiptBalance > 0 || userBalances.gladiatorReceiptBalance > 0)) {

    }
    else {
      availableUnstakes = userBalances.traderReceiptBalance + userBalances.fabricReceiptBalance + userBalances.gladiatorReceiptBalance;
    }

    // console.log("availableRedemptions", availableRedemptions);
    // console.log("availableUnstakes", availableUnstakes);
    return [availableRedemptions, availableUnstakes];
  }, [wallet.publicKey, userBalances]);

  useEffect(() => {
    if (isRedemptionOpen) {
      setDisplayRedemptions(availableRedemptions);
      setUserSelectedAmount(availableRedemptions);
    }
    else if (isUnstakeOpen) {
      setDisplayRedemptions(availableUnstakes);
      setUserSelectedAmount(availableUnstakes);
    }
    else if (!isRedemptionOpen && !isUnstakeOpen) {
      setDisplayRedemptions(availableUnstakes);
      setUserSelectedAmount(availableUnstakes);
    }
    handleSliderChange();

  }, [isRedemptionOpen, isUnstakeOpen, availableRedemptions, availableUnstakes])

  // On Collection Change
  useEffect(() => {
    if (connection) {
      connection.getTokenSupply(
        new PublicKey("GsLjbeyLeawNhSXtPrWW3qZa5fHCGPMbMdML4E5FVEWR"))
        .then((supply) => {
          // console.log(supply.value.uiAmount);
          setMedallionsLockedAmount(supply.value.uiAmount * 2);
          setCurrentRedemptionsAmount(supply.value.uiAmount);
        });

    }
    if (!wallet.publicKey) {
      return;
    }
    else {
      fetchUserBalances();
    }
  }, [wallet.publicKey, connection]);

  const fetchUserBalances = () => {
    getUserBalancesEndpoint.mutateAsync({ initializer: wallet.publicKey.toBase58() })
      .then((res) => {
        setUserBalances(res);
      });
  }

  // Claim
  const handleRedeemClick = async () => {
    if (!wallet.publicKey) {
      toast.error("Please connect your wallet");
      return;
    }
    setStatus("pending");
    // console.log(`Redeeming ${selectedRedemptionsAmount} tokens`);

    if (isRedemptionOpen) {
      await handleRedeem();
    }
    else if (isUnstakeOpen) {
      await handleUnstake();
    }
    else if (!isRedemptionOpen && !isUnstakeOpen) {
      await handleFinalize();
    }
  };

  const handleRedeem = async () => {
    if (userSelectedAmount > availableRedemptions) {
      toast.error("You don't have enough available redemptions");
      setStatus("idle");
      return;
    }

    try {
      toast.success(`Generating transaction to initiate ${userSelectedAmount} redemptions`, {
        id: "redemption",
        duration: Infinity
      });

      const redemptions = new Array(userSelectedAmount).fill({
        guild: selectedRedemptionType
      });

      getRedeemTransactionsEndpoint.mutateAsync({
        initializer: wallet.publicKey.toBase58(),
        redemptions: [...redemptions]
      })
        .then(async (res) => {
          const transactions = res.redeemTokenTransactionAndTokens.map((redemptionTXandTokens: any) => {
            return Transaction.from(Buffer.from(redemptionTXandTokens.transaction, 'base64'));
          });
          // return transactions;
          toast.loading(`Awaiting your approval to initiate ${transactions.length} redemptions`, {
            id: "redemption",
            duration: Infinity
          });

          const signedTxs = await wallet.signAllTransactions(transactions)

          const signedSerializedTxs = signedTxs.map((signedTx) => {
            return signedTx.serialize().toString('base64');
          });

          toast.loading(`Sending transaction to initiate ${signedSerializedTxs.length} redemptions`, {
            id: "redemption",
            duration: Infinity
          });

          const signedSerializedTxsWithTokens = signedSerializedTxs.map((signedSerializedTx, index) => {
            return {
              signedSerializedTx,
              tokens: res.redeemTokenTransactionAndTokens[index].tokens
            }
          })
          sendTransactionsEndpoint.mutateAsync({
            signedSerializedTxsWithTokens
          })
            .then((res) => {
              const successfulRedemptions = res.sendTXRes.length;
              if (successfulRedemptions === 0) {
                toast.error("No successful redemptions initiated, please try again", {
                  id: "redemption",
                  duration: 4000
                });
                return;
              }
              toast.success(`${successfulRedemptions} redemption${successfulRedemptions > 1 ? "s" : ""} successfully initiated`, {
                id: "redemption",
                duration: 4000
              })
              setCurrentRedemptionsAmount(currentRedemptionsAmount + successfulRedemptions);
              // setAvailableRedemptions(availableRedemptions.slice(successfulRedemptions));
              setMedallionsLockedAmount(medallionsLockedAmount + (successfulRedemptions * 2));
              setUserBalances({
                ...userBalances,
                medallionsHeld: userBalances.medallionsHeld - (successfulRedemptions * 2),
                gobiBalance: userBalances.gobiBalance - (successfulRedemptions * 200),
              })
              setStatus("idle");
              setUserSelectedAmount(1);
              connection.confirmTransaction(res.sendTXRes[0].signature, "finalized")
                .then(() => {
                  fetchUserBalances();
                })

              // await connection.confirmTransaction(res.sendRedemptionRes[0].txid);
            })

        })
        .catch((e) => {
          toast.error("Failed to get transactions", {
            id: "redemption",
            duration: 4000
          });
          setStatus("idle");
          return;
        })
    }
    catch (e) {
      const errorMessage = e?.message ?? "Unknown Error";
      toast.error(`Failed to redeem due to following error: ${errorMessage}, please try again`, {
        id: "redemption",
        duration: 4000
      });
      setStatus("idle");
    }
  }

  const handleUnstake = async () => {

    if (userSelectedAmount > availableUnstakes) {
      toast.error("You don't have enough unstakes available");
      setStatus("idle");

      return;
    }
    try {
      toast.success(`Generating transaction to unstake ${userSelectedAmount * 2} medallions`, {
        id: "redemption",
        duration: Infinity
      });

      const unstakes = new Array(userSelectedAmount).fill({
        guild: selectedRedemptionType
      });

      getUnstakeTransactionsEndpoint.mutateAsync({
        initializer: wallet.publicKey.toBase58(),
        unstakes: [...unstakes]
      })
        .then(async (res) => {
          const transactions = res.unredeemTokenTransactionAndTokens.map((redemptionTXandTokens: any) => {
            return Transaction.from(Buffer.from(redemptionTXandTokens.transaction, 'base64'));
          });
          // return transactions;
          toast.loading(`Awaiting your approval to unstake ${userSelectedAmount * 2} medallions`, {
            id: "redemption",
            duration: Infinity
          });

          const signedTxs = await wallet.signAllTransactions(transactions)

          const signedSerializedTxs = signedTxs.map((signedTx) => {
            return signedTx.serialize().toString('base64');
          });

          toast.loading(`Sending transaction to unstake ${signedSerializedTxs.length * 2} medallions`, {
            id: "redemption",
            duration: Infinity
          });

          const signedSerializedTxsWithTokens = signedSerializedTxs.map((signedSerializedTx, index) => {
            return {
              signedSerializedTx,
              tokens: res.unredeemTokenTransactionAndTokens[index].tokens
            }
          })
          sendTransactionsEndpoint.mutateAsync({
            signedSerializedTxsWithTokens
          })
            .then((res) => {
              const successfulRedemptions = res.sendTXRes.length;
              if (successfulRedemptions === 0) {
                toast.error("No medallions successfully unstaked, please try again", {
                  id: "redemption",
                  duration: 4000
                });
                return;
              }
              toast.success(`${successfulRedemptions * 2} medallions successfully unstaked`, {
                id: "redemption",
                duration: 4000
              })
              setCurrentRedemptionsAmount(currentRedemptionsAmount - successfulRedemptions);
              // setAvailableRedemptions(availableRedemptions.slice(successfulRedemptions));
              setMedallionsLockedAmount(medallionsLockedAmount - (successfulRedemptions * 2));
              setUserBalances({
                ...userBalances,
                medallionsHeld: userBalances.medallionsHeld + (successfulRedemptions * 2),
                gobiBalance: userBalances.gobiBalance + (successfulRedemptions * 200),
                traderReceiptBalance: userBalances.traderReceiptBalance - (successfulRedemptions),
              })
              setStatus("idle");
              setUserSelectedAmount(1);
              connection.confirmTransaction(res.sendTXRes[0].signature, "finalized")
                .then(() => {
                  fetchUserBalances();
                })

              // await connection.confirmTransaction(res.sendRedemptionRes[0].txid);
            })

        })
        .catch((e) => {
          toast.error(`Failed to get transactions`, {
            id: "redemption",
            duration: 4000
          });
          setStatus("idle");

          return;
        })
    }
    catch (e) {
      const errorMessage = e?.message ?? "Unknown Error";
      toast.error(`Failed to redeem due to following error: ${errorMessage}, please try again`, {
        id: "redemption",
        duration: 4000
      });
      setStatus("idle");
    }
  }

  const handleFinalize = async () => {

    if (userSelectedAmount > availableUnstakes) {
      toast.error("You don't have enough vouchers available");
      setStatus("idle");

      return;
    }
    try {
      toast.success(`Generating transaction to redeem ${userSelectedAmount} vouchers`, {
        id: "redemption",
        duration: Infinity
      });

      const burns = new Array(userSelectedAmount).fill({
        guild: selectedRedemptionType
      });

      getFinalizeTransactionsEndpoint.mutateAsync({
        initializer: wallet.publicKey.toBase58(),
        burns: [...burns]
      })
        .then(async (res) => {
          const transactions = res.finalizeTxandTokens.map((redemptionTXandTokens: any) => {
            return Transaction.from(Buffer.from(redemptionTXandTokens.transaction, 'base64'));
          });
          // return transactions;
          toast.loading(`Awaiting your approval to redeem ${userSelectedAmount} vouchers`, {
            id: "redemption",
            duration: Infinity
          });

          const signedTxs = await wallet.signAllTransactions(transactions)

          const signedSerializedTxs = signedTxs.map((signedTx) => {
            return signedTx.serialize().toString('base64');
          });

          toast.loading(`Sending transaction to redeem ${signedSerializedTxs.length} vouchers`, {
            id: "redemption",
            duration: Infinity
          });

          const signedSerializedTxsWithTokens = signedSerializedTxs.map((signedSerializedTx, index) => {
            return {
              signedSerializedTx,
              tokens: res.finalizeTxandTokens[index].tokens
            }
          })
          sendTransactionsEndpoint.mutateAsync({
            signedSerializedTxsWithTokens
          })
            .then((res) => {
              const successfulRedemptions = res.sendTXRes.length;
              if (successfulRedemptions === 0) {
                toast.error("No vouchers successfully redeemed, please try again", {
                  id: "redemption",
                  duration: 4000
                });
                return;
              }
              toast.success(`${successfulRedemptions} voucher${successfulRedemptions > 1 ? "s" : ""} successfully redeemed`, {
                id: "redemption",
                duration: 4000
              })
              setCurrentRedemptionsAmount(currentRedemptionsAmount - successfulRedemptions);
              // setAvailableRedemptions(availableRedemptions.slice(successfulRedemptions));
              setMedallionsLockedAmount(medallionsLockedAmount - (successfulRedemptions * 2));
              setUserBalances({
                ...userBalances,
                solBalance: userBalances.solBalance + (successfulRedemptions * ((currentVaultSize / currentRedemptionsAmount))),
                traderReceiptBalance: userBalances.traderReceiptBalance - (successfulRedemptions),
              })
              setStatus("idle");
              setUserSelectedAmount(1);
              connection.confirmTransaction(res.sendTXRes[0].signature, "finalized")
                .then(() => {
                  fetchUserBalances();
                })

              // await connection.confirmTransaction(res.sendRedemptionRes[0].txid);
            })

        })
        .catch((e) => {
          toast.error(`Failed to get transactions`, {
            id: "redemption",
            duration: 4000
          });
          setStatus("idle");

          return;
        })
    }
    catch (e) {
      const errorMessage = e?.message ?? "Unknown Error";
      toast.error(`Failed to redeem due to following error: ${errorMessage}, please try again`, {
        id: "redemption",
        duration: 4000
      });
      setStatus("idle");

    }
  }

  const handleSliderChange = () => {
    if (sliderInputRef.current) {
      setUserSelectedAmount(parseInt(sliderInputRef.current.value));
    }
  };

  const isMounted = useIsMounted();

  if (!isMounted) {
    return null;
  }


  return (
    <>
      <Head>
        <title>Ceremony : Merchants of Gobi</title>
        <meta name="description" content="Merchants of Gobi Ceremony" />
      </Head>
      <StyledCeremonyPage className="ceremonyPage">

        <MaxWidthWrapper className="ceremonyPage-wrapper">

          <div className="ceremonyPage-content">

            <div className="ceremonyPage-text">

              {isRedemptionOpen ?

                <>
                  <p>
                    Next Ceremony Begins in&nbsp;
                    <em>
                      <Countdown
                        date={initiateEndTime}
                        renderer={renderGoLiveDateCounter}
                        onComplete={() => {
                          setIsRedemptionOpen(false)
                        }}
                      />&nbsp;
                    </em>
                    <br />
                    where you&apos;ll have 24 hours to cancel your redemption. The claim period ends in&nbsp;
                    <em>
                      <Countdown
                        date={ceremonyEndTime}
                        renderer={renderGoLiveDateCounter}
                        onComplete={() => {
                          setIsRedemptionOpen(false)
                        }}
                      />
                    </em>.
                  </p>

                  <p>
                    Price per Redemption is <em>2 Medallions</em> &amp; <em>200 $GOBI</em><br />
                    and has been joined <em>{(medallionsLockedAmount / 2) ?? 0}</em> times.
                  </p>

                  <p>
                    <em>{currentVaultSize.toFixed(4)} SOL</em> will be distributed for this Ceremony
                  </p>

                </>

                :
                isUnstakeOpen ?

                  <>
                    <p>
                      You have&nbsp;
                      <em>
                        <Countdown
                          date={unstakeEndTime}
                          renderer={renderGoLiveDateCounter}
                          onComplete={() => {
                            setIsUnstakeOpen(false)
                          }}
                        />
                      </em>&nbsp;
                      left to unstake your Medallions and abandon your claim.
                    </p>

                    <p>
                      Price per Redemption is <em>2 Medallions</em> &amp; <em>200 $GOBI</em><br />
                      and has been joined <em>{(medallionsLockedAmount / 2) ?? 0}</em> times.
                    </p>

                    <p>
                      Ceremony split will be <em>{ceremonySplit.toFixed(4)} SOL</em>
                    </p>

                  </>

                  :

                  <>
                    <p>
                      Current ceremony ends in&nbsp;
                      <em>
                        <Countdown
                          date={ceremonyEndTime}
                          renderer={renderGoLiveDateCounter}
                          onComplete={() => {
                            setIsRedemptionOpen(true)
                          }}
                        />
                      </em>
                    </p>

                    <p>
                      Price per Redemption was <em>2 Medallions</em> &amp; <em>200 $GOBI</em><br />
                      and has <em>{(medallionsLockedAmount / 2) ?? 0}</em> remaining claims.
                    </p>

                    <p>
                      Ceremony split is <em>{ceremonySplit.toFixed(4)} SOL</em> per redemption
                    </p>
                  </>

              }

            </div>

            {wallet.connected ? (
              <>
                <div className="ceremonyPage-redemptions">
                  {
                    displayRedemptions > 1 ?

                      // true
                      <div className="ceremonyPage-redemptions-slider">
                        <span>{userSelectedAmount < 10 ? "0" + userSelectedAmount : userSelectedAmount}</span>
                        <StyledSlider type="range" ref={sliderInputRef} min="1" max={displayRedemptions} onChange={handleSliderChange} className="slider" id="myRange" />
                        <span>{!displayRedemptions ? "01" : displayRedemptions < 10 ? "0" + displayRedemptions : displayRedemptions}</span>
                      </div> :

                      // False
                      <div className="ceremonyPage-redemptions-label">
                        Your Available Redemptions: <strong>{displayRedemptions > 0 ? displayRedemptions : 0}</strong>
                      </div>

                  }
                </div>
                <div className="ceremonyPage-redeem">
                  <button onClick={handleRedeemClick} disabled={status !== "idle"} className='ceremonyPage-layout__redemption-button'>
                    <span>{isRedemptionOpen ? "JOIN CEREMONY" : isUnstakeOpen ? "CANCEL INITIATED CEREMONY" : "REDEEM (CLAIM SOL)"}</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="ceremonyPage-connect">
                <WalletButton>
                  {({ connecting, connected, shortAddress, wallet }) => (
                    <>
                      {connecting ? (
                        <span>Connecting...</span>
                      ) : connected ? (
                        <>{shortAddress}</>
                      ) : (
                        <span>Connect Wallet</span>
                      )}
                    </>
                  )}
                </WalletButton>
              </div>
            )}

          </div>

        </MaxWidthWrapper>

      </StyledCeremonyPage>
    </>
  );

};

const renderGoLiveDateCounter = ({ days, hours, minutes, seconds }: any) => {
  return (
    <>
      {/* <h1 style={{ textAlign: "center" }}>
        Redemption begins in
      </h1> */}
      <span className="countdown">
        <span><span>{days}</span> Days</span>
        <span><span>{hours}</span> Hours</span>
        <span><span>{minutes}</span> Min</span>
        <span><span>{seconds}</span></span>
      </span>
    </>

  );
};


export const getStaticProps = async ({ params }) => {

  const ssh = createServerSideHelpers({
    router: appRouter,
    ctx: await createContext(),
    transformer: superJson
  });

  return {
    props: {
      trpcState: ssh.dehydrate(),
    },
    revalidate: 1
  };

};


CeremonyPage.getLayout = (page: ReactElement) => {
  return (
    <MainLayout>
      <WalletLayout>
        {page}
      </WalletLayout>
    </MainLayout>
  )
}


export default CeremonyPage;


const StyledCeremonyPage = styled.div`

  padding: 1rem 1rem 0 1rem;

  .ceremonyPage-wrapper{
    max-width: 75rem;
  }

  .ceremonyPage-content{

    padding: 3rem 0;

    .ceremonyPage-text{

      display: flex;
      flex-direction: column;
      gap: 2.5rem;
      color: ${color.gray(40)};
      font-size: 1.5rem;
      font-weight: 600;
      line-height: 2rem;

      em{
        color: ${color.gray(100)};
        font-style: normal;
      }

    }

    .ceremonyPage-redemptions{

      margin-top: 4rem;

      .ceremonyPage-redemptions-slider{
        display: flex;
        align-items: center;
        max-width: 32rem;
        gap: 1rem;
        margin-top: 3rem;
        margin-bottom: 2rem;
      }

    }

    .ceremonyPage-redeem{
      
      margin-top: 1rem;

      button{
        display: flex;
        justify-content: center;
        align-items: center;
        height: 3rem;
        width: 100%;
        color: black;
        font-size: 0.875rem;
        font-weight: bold;
        border-radius: 0.5rem;
        background-color: ${color.gray(100)};
        transition: all 0.15s ease-in-out;
      }

    }

    .ceremonyPage-connect{

      margin-top: 4rem;

      --connect-wallet-button-height:3rem;
      --connect-wallet-button-font-size: 0.875rem;
      --connect-wallet-button-color: ${color.gray(100)};
      --connect-wallet-button-hover-color: ${color.gray(100)};

      button{
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        color: black;
      }


    }

  }


  .countdown{

    display: inline-flex;
    flex-direction: row;
    gap: 0.75rem;

    div{
      width: 33.3%;
      display: inline-flex;
    }

  }


  @media (min-width: ${bp(3)}) {
  
    padding: 3rem 1.5rem 6rem 1.5rem;

    .ceremonyPage-content{

      padding: 10rem 0;

      .ceremonyPage-text{
        gap: 3rem;
        font-size: 2rem;
        line-height: 3rem;
      }

      .ceremonyPage-redemptions{}

      .ceremonyPage-redeem{
        
        margin-top: 1rem;

        button{

          width: auto;
          height: 4rem;
          min-width: 16rem;
          font-size: 1rem;
          background-color: ${color.gray(80)};

          &:hover{
            background-color: ${color.gray(100)};
          }

        }

      }

      .ceremonyPage-connect{

        --connect-wallet-button-height: 4rem;
        --connect-wallet-button-font-size: 1rem;
        --connect-wallet-button-color: ${color.gray(80)};
        --connect-wallet-button-hover-color: ${color.gray(100)};

        button{
          width: auto;
          min-width: 16rem;
        }

      }

    }
  
  }

  @media (min-width: ${bp(4)}) {
  
    padding: 3rem 2rem 6rem 2rem;

    .ceremonyPage-content{

      .ceremonyPage-text{
        font-size: 2.5rem;
        line-height: 3.5rem;
      }

    }

  }

  
`


// Styled Slider
const StyledSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  height: 1px;
  width: 100%;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    background: hsla(0, 0%, 100%, 1);
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
  }

  
`